import React from 'react';
import theme from './BuyerMassInviteTable.module.scss';
import { Trans } from '@lingui/react/macro';
import { ChangedItem, TranslatableText } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { BuyerMassImportRow, BuyerImportRowSendStatus } from '.';
import { Button } from '@/components/Buttons';
import { faRedo, faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Indicator, { IndicatorColor } from '@/components/Indicator';
import { classnames } from '@/utils/html';
import { ParsedBuyerImportRowStatus, BuyerImportPhoneNumberStatus } from '@/services/projects';
import { Checkbox, isSalutatable } from '@ygtechnologies/bygr-frontend-component-library';
import { useGetActiveProject } from '@/utils/hooks';

interface BuyerMassInviteTableRowProps {
    row: BuyerMassImportRow;
    onChange?(change: ChangedItem<BuyerMassImportRow>): void;
    onRetryClick?(row: BuyerMassImportRow): void;
}

const ErrorIndicator: React.FC<{}> = () => {
    return <FontAwesomeIcon icon={faExclamationCircle} className={theme.errorIndicator} />;
};

type StatusMap = {
    [key in keyof typeof BuyerImportRowSendStatus]?: {
        display: TranslatableText;
        color: IndicatorColor;
    };
};

const BuyerMassInviteTableRow: React.FC<BuyerMassInviteTableRowProps> = ({ row, onChange, onRetryClick }) => {
    const project = useGetActiveProject();

    const handleChange = (changes: Partial<BuyerMassImportRow>) => {
        triggerHandler(onChange, ChangedItem.fromMerge(row, changes));
    };

    const handleRetryClick = () => {
        triggerHandler(onRetryClick, row);
    };

    const statusMap: StatusMap = {
        [BuyerImportRowSendStatus.Ready]: {
            display: <Trans>Ready</Trans>,
            color: 'secondary',
        },
        [BuyerImportRowSendStatus.Queued]: {
            display: <Trans>Queued</Trans>,
            color: 'warning',
        },
        [BuyerImportRowSendStatus.Sending]: {
            display: <Trans>Sending</Trans>,
            color: 'warning',
        },
        [BuyerImportRowSendStatus.Failed]: {
            display: (
                <Button color="text" icon={faRedo} onClick={handleRetryClick}>
                    <Trans>Retry</Trans>
                </Button>
            ),
            color: 'danger',
        },
        [BuyerImportRowSendStatus.Sent]: {
            display: row.shouldInvite ? <Trans>Sent</Trans> : <Trans>Buyer added without invite</Trans>,
            color: 'success',
        },
    };

    const renderStatus = () => {
        const data = statusMap[row.sendStatus];

        if (row.status === ParsedBuyerImportRowStatus.AlreadyHasBuyer) {
            return <Trans>Buyer already exists</Trans>;
        } else if (row.status === ParsedBuyerImportRowStatus.InvalidUnit) {
            return <Trans>Invalid unit</Trans>;
        }

        if (!data) {
            return null;
        }

        return (
            <>
                <Indicator color={data.color} className={theme.statusIndicator} />
                {data.display}
            </>
        );
    };

    const disabled = row.status !== ParsedBuyerImportRowStatus.Ok;
    return (
        <tr key={row.residentialUnitId} className={classnames({ [theme.disabled]: disabled })}>
            <td>{row.residentialUnitName || <Trans>N/A</Trans>}</td>
            <td>{row.firstName}</td>
            <td>{row.lastName}</td>
            <td>
                {row.email}
                {!row.validation.emailValid && <ErrorIndicator />}
            </td>
            <td>
                {row.phoneNumber}
                {row.phoneNumberStatus === BuyerImportPhoneNumberStatus.CountryCodeNotValid && (
                    <span className={theme.errorText}>
                        <Trans>(Country code not set)</Trans>
                    </span>
                )}
                {row.phoneNumberStatus === BuyerImportPhoneNumberStatus.PhoneNumberNotValid && (
                    <span className={theme.errorText}>
                        <Trans>(Invalid phone number)</Trans>
                    </span>
                )}
                {!row.validation.phoneValid && <ErrorIndicator />}
            </td>
            <td>
                <Checkbox
                    checked={row.inviteSms}
                    disabled={disabled}
                    onChange={(e) => handleChange({ inviteSms: e.target.checked })}
                />
            </td>
            {/* <td>
                <Checkbox
                    checked={row.inviteEmail}
                    disabled={disabled}
                    onChange={(e) => handleChange({ inviteEmail: e.target.checked })}
                />
            </td> */}
            <td className={theme.status}>{renderStatus()}</td>
            {isSalutatable(project?.countryCode) && (
                <>
                    <td>{row.salutation}</td>
                    <td>{row.title}</td>
                </>
            )}
        </tr>
    );
};

interface BuyerMassInviteTableProps {
    data: BuyerMassImportRow[];
    onChange?(newData: BuyerMassImportRow[]): void;
    onRetryClick?(row: BuyerMassImportRow): void;
}

const BuyerMassInviteTable: React.FC<BuyerMassInviteTableProps> = ({ data, onChange, onRetryClick }) => {
    const project = useGetActiveProject();

    const handleRowChange = (change: ChangedItem<BuyerMassImportRow>) => {
        triggerHandler(onChange, change.updateArray(data));
    };

    const handleInviteSmsChange = (flag: boolean) => {
        triggerHandler(
            onChange,
            data.map((row) => ({ ...row, inviteSms: flag }))
        );
    };

    // const handleInviteEmailChange = (flag: boolean) => {
    //     triggerHandler(
    //         onChange,
    //         data.map((row) => ({ ...row, inviteEmail: flag }))
    //     );
    // };

    return (
        <table className="table style-1 card rounded">
            <thead>
                <tr>
                    <th>
                        <Trans>Unit</Trans>
                    </th>
                    <th>
                        <Trans>First name</Trans>
                    </th>
                    <th>
                        <Trans>Last name</Trans>
                    </th>
                    <th>
                        <Trans>Email</Trans>
                    </th>
                    <th>
                        <Trans>Phone number</Trans>
                    </th>
                    <th>
                        <Checkbox
                            onChange={(e) => handleInviteSmsChange(e.target.checked)}
                            checked={data.every((x) => x.inviteSms)}>
                            <Trans>Invite by SMS</Trans>
                        </Checkbox>
                    </th>
                    {/* <th>
                        <Checkbox
                            onChange={(e) => handleInviteEmailChange(e.target.checked)}
                            checked={data.every((x) => x.inviteEmail)}>
                            <Trans>Invite by email</Trans>
                        </Checkbox>
                    </th> */}
                    <th>
                        <Trans>Status</Trans>
                    </th>
                    {isSalutatable(project?.countryCode) && (
                        <>
                            <th>
                                <Trans>Salutation</Trans>
                            </th>
                            <th>
                                <Trans>Title</Trans>
                            </th>
                        </>
                    )}
                </tr>
            </thead>

            <tbody>
                {data.map((row) => (
                    <BuyerMassInviteTableRow
                        row={row}
                        key={row.rowId}
                        onChange={handleRowChange}
                        onRetryClick={onRetryClick}
                    />
                ))}
            </tbody>
        </table>
    );
};

export default BuyerMassInviteTable;
